"use client";
import React, { useEffect, useRef, useState, useCallback } from "react";
import Modal from "../../atoms/Modal";
import { PopupComponentProps } from "./types";
import "./styles.scss";

const PopupComponent: React.FC<PopupComponentProps> = ({
  children,
  className = "",
  onClose,
  onClickOutside,
  onKeypressEsc,
  onLoad,
}) => {
  const [isModalOpen, setModalOpen] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  // Optimized event handler for close icon click
  const handleClickClose = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const closestButton = target.closest("button");
      if (closestButton?.classList.contains("alertPopup__close") && typeof onClose === "function") {
        setModalOpen(false);
      }
    },
    [onClose]
  );

  // Handle click outside the modal
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        typeof onClickOutside === "function"
      ) {
        onClickOutside(event);
        setModalOpen(false);
      }
    },
    [onClickOutside]
  );

  // Handle Escape key press
  const handleEsc = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape" && typeof onKeypressEsc === "function") {
        onKeypressEsc(event);
        setModalOpen(false);
      }
    },
    [onKeypressEsc]
  );

  // Register and cleanup event listeners
  useEffect(() => {
    document.addEventListener("click", handleClickClose);
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keyup", handleEsc);

    return () => {
      document.removeEventListener("click", handleClickClose);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keyup", handleEsc);
    };
  }, [handleClickClose, handleClickOutside, handleEsc]);

  // Invoke onLoad callback when the component loads
  useEffect(() => {
    if (typeof onLoad === "function") {
      onLoad();
    }
  }, [onLoad]);

  return (
    <Modal
      isOpen={isModalOpen}
      className="modal">
      <div
        ref={containerRef}
        className={`popup ${className}`}>
        {children}
      </div>
    </Modal>
  );
};

export default PopupComponent;
